import { computed, reactive, ref, unref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import {
  LIFECYCLE_ACTION_LABELS,
  LISTING_SOURCE_LABELS,
  LISTING_STATUS_LABELS,
  RENTAL_ADMINISTRATOR_LABELS,
} from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  confirmSensitiveAction,
  formatDateTime,
  normalizeDiffEntries,
  normalizePayload,
  previewActionName,
} from '../utils/imovelShowFormatters';

/**
 * Ciclo de vida do anúncio: versões, diff, prévia e ações sensíveis.
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {import('vue').Ref<object>} options.additionalAttributes
 * @param {import('vue').Ref<boolean>} options.mediaQaCanPublish
 * @param {import('vue').Ref<string>} options.mediaReadinessBlocker
 * @param {Function} options.onLifecycleApplied efeitos após ação concluída
 */
export function useImovelListingLifecycle({
  imovel,
  additionalAttributes,
  mediaQaCanPublish,
  mediaReadinessBlocker,
  onLifecycleApplied,
}) {
  const route = useRoute();
  const { t } = useI18n();

  const listingLifecycle = ref(null);
  const listingVersions = ref([]);
  const listingLoading = ref(false);
  const listingError = ref('');
  const lifecyclePreview = ref(null);
  const lifecyclePreviewLoading = ref(false);
  const lifecycleCommandKeys = reactive({});
  const lifecycleLoading = ref('');
  const lifecycleReason = ref('manual_update');
  const suspendDays = ref(7);
  const versionSuccessMessage = ref('');

  const primaryListingId = computed(
    () => listingLifecycle.value?.id || unref(imovel)?.primaryListing?.id
  );
  const currentListing = computed(
    () => listingLifecycle.value || unref(imovel)?.primaryListing || {}
  );
  const currentListingModalidade = computed(
    () => currentListing.value?.modalidade || unref(imovel)?.finalidade
  );
  const listingStatusLabel = computed(
    () =>
      LISTING_STATUS_LABELS[
        currentListing.value?.listingStatus || unref(imovel)?.listingStatus
      ] ||
      currentListing.value?.listingStatus ||
      unref(imovel)?.listingStatus ||
      'Não informado'
  );
  const rentalAdministratorLabel = computed(
    () =>
      RENTAL_ADMINISTRATOR_LABELS[unref(imovel)?.rentalAdministrator] ||
      unref(imovel)?.rentalAdministrator ||
      'Não informado'
  );
  const listingSourceLabel = computed(
    () =>
      LISTING_SOURCE_LABELS[unref(imovel)?.listingSource] ||
      unref(imovel)?.listingSource ||
      'Não informado'
  );
  const visitasPermitidasLabel = computed(() =>
    unref(imovel)?.visitasPermitidas
      ? 'Visitas liberadas'
      : 'Visitas bloqueadas'
  );

  const listingVersionRecords = computed(() => {
    const records = listingVersions.value.length
      ? listingVersions.value
      : currentListing.value?.versionSummary ||
        unref(imovel)?.listingVersions ||
        unref(additionalAttributes).listingVersions ||
        unref(additionalAttributes).listing_versions ||
        unref(additionalAttributes).listingVersionHistory ||
        unref(additionalAttributes).listing_version_history ||
        [];
    return Array.isArray(records)
      ? records.map(record => normalizePayload(record))
      : [];
  });
  const latestListingVersion = computed(() => listingVersionRecords.value[0]);
  const listingLifecycleTimeline = computed(() => {
    const records = currentListing.value?.lifecycleTimeline?.length
      ? currentListing.value.lifecycleTimeline
      : listingVersionRecords.value;

    return Array.isArray(records)
      ? records
          .map(record => normalizePayload(record))
          .filter(record => record.changeKind || record.lifecycleEvent)
          .slice(0, 6)
      : [];
  });
  const latestListingDiffEntries = computed(() =>
    normalizeDiffEntries(latestListingVersion.value?.diffFromPrevious)
  );
  const lifecyclePreviewDiffEntries = computed(() =>
    normalizeDiffEntries(lifecyclePreview.value?.diff)
  );
  const lifecyclePreviewLabel = computed(() => {
    if (!lifecyclePreview.value) return 'Prévia ainda não gerada';

    return `v${lifecyclePreview.value.currentVersion} → v${lifecyclePreview.value.nextVersion}`;
  });
  const listingVersionEvidenceLabel = computed(() => {
    if (!latestListingVersion.value) {
      return t('PROPERTIES.READINESS.VERSION.EMPTY');
    }

    const version =
      latestListingVersion.value.version ||
      latestListingVersion.value.versionNumber ||
      latestListingVersion.value.id;
    const actor =
      latestListingVersion.value.authorName ||
      latestListingVersion.value.userName ||
      latestListingVersion.value.actorName ||
      '';
    const changedAt = formatDateTime(
      latestListingVersion.value.createdAt ||
        latestListingVersion.value.updatedAt ||
        latestListingVersion.value.changedAt
    );
    return [
      version ? t('PROPERTIES.READINESS.VERSION.LABEL', { version }) : null,
      actor,
      changedAt,
    ]
      .filter(Boolean)
      .join(' · ');
  });

  const suspendUntilIso = () => {
    const days = Number(suspendDays.value);
    const normalizedDays = Number.isFinite(days) && days > 0 ? days : 7;
    const date = new Date();
    date.setDate(date.getDate() + normalizedDays);
    return date.toISOString();
  };

  const fetchListingLifecycle = async () => {
    if (!route.params.id || !primaryListingId.value) {
      listingLifecycle.value = null;
      listingVersions.value = [];
      return;
    }

    listingLoading.value = true;
    listingError.value = '';
    try {
      const [listingResponse, versionsResponse] = await Promise.all([
        ImovelAPI.listing(route.params.id, primaryListingId.value),
        ImovelAPI.listingVersions(route.params.id, primaryListingId.value),
      ]);
      listingLifecycle.value = normalizePayload(listingResponse.data?.payload);
      listingVersions.value = (versionsResponse.data?.payload || []).map(
        version => normalizePayload(version)
      );
    } catch (error) {
      listingError.value = actionErrorMessage(
        error,
        'Não foi possível carregar versões do anúncio.'
      );
    } finally {
      listingLoading.value = false;
    }
  };

  const lifecyclePreviewMatches = action =>
    lifecyclePreview.value?.requestedAction === action ||
    lifecyclePreview.value?.action === previewActionName(action);

  const lifecycleIdempotencyKey = action => {
    if (!lifecycleCommandKeys[action]) {
      const random =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      lifecycleCommandKeys[action] = [
        'listing-lifecycle',
        route.params.id,
        primaryListingId.value || 'legacy',
        action,
        random,
      ].join(':');
    }

    return lifecycleCommandKeys[action];
  };

  const lifecycleActionPayload = action => ({
    reason: lifecycleReason.value,
    modalidade: currentListingModalidade.value,
    idempotency_key: lifecycleIdempotencyKey(action),
    suspended_until: action === 'suspend' ? suspendUntilIso() : undefined,
  });

  const clearLifecycleActionState = action => {
    delete lifecycleCommandKeys[action];
    if (lifecyclePreview.value?.requestedAction === action) {
      lifecyclePreview.value = null;
    }
  };

  const fetchLifecyclePreview = async action => {
    if (!primaryListingId.value) {
      lifecyclePreview.value = null;
      return;
    }

    lifecyclePreviewLoading.value = true;
    listingError.value = '';
    versionSuccessMessage.value = '';
    try {
      const { data } = await ImovelAPI.listingLifecyclePreview(
        route.params.id,
        primaryListingId.value,
        {
          preview_action: previewActionName(action),
          reason: lifecycleReason.value,
          modalidade: currentListingModalidade.value,
          suspended_until: action === 'suspend' ? suspendUntilIso() : undefined,
        }
      );
      lifecyclePreview.value = {
        ...normalizePayload(data?.payload),
        requestedAction: action,
      };
      versionSuccessMessage.value =
        'Prévia de versão gerada. Revise o diff antes da ação sensível.';
    } catch (error) {
      listingError.value = actionErrorMessage(
        error,
        'Não foi possível gerar a prévia do anúncio.'
      );
      useAlert(listingError.value);
    } finally {
      lifecyclePreviewLoading.value = false;
    }
  };

  const runLifecycleAction = async action => {
    if (action === 'publish' && !unref(mediaQaCanPublish)) {
      useAlert(
        unref(mediaReadinessBlocker) || 'Mídia bloqueada para publicação.'
      );
      return;
    }

    if (
      !confirmSensitiveAction(
        `Confirmar ${LIFECYCLE_ACTION_LABELS[action] || action} este anúncio? Motivo: ${lifecycleReason.value}.`
      )
    ) {
      return;
    }

    lifecycleLoading.value = action;
    versionSuccessMessage.value = '';
    try {
      const payload = lifecycleActionPayload(action);
      if (action === 'publish') {
        await ImovelAPI.publish(route.params.id, payload);
        versionSuccessMessage.value = 'Anúncio publicado e versão registrada.';
      } else if (action === 'unpublish') {
        await ImovelAPI.unpublish(route.params.id, payload);
        versionSuccessMessage.value =
          'Anúncio despublicado e versão registrada.';
      } else if (action === 'suspend') {
        await ImovelAPI.suspendListing(route.params.id, payload);
        versionSuccessMessage.value =
          'Anúncio suspenso temporariamente e versão registrada.';
      } else if (action === 'relist') {
        await ImovelAPI.relist(route.params.id, payload);
        versionSuccessMessage.value =
          'Reanúncio solicitado e versão registrada.';
      }
      useAlert(versionSuccessMessage.value);
      clearLifecycleActionState(action);
      await onLifecycleApplied();
    } catch (error) {
      useAlert(
        actionErrorMessage(error, 'Não foi possível atualizar o anúncio.')
      );
    } finally {
      lifecycleLoading.value = '';
    }
  };

  const prepareOrRunLifecycleAction = async action => {
    if (action === 'publish' && !lifecyclePreviewMatches(action)) {
      await fetchLifecyclePreview(action);
      if (lifecyclePreview.value) {
        useAlert(
          'Prévia de publicação gerada. Revise o diff antes de publicar.'
        );
      }
      return;
    }

    await runLifecycleAction(action);
  };

  watch([lifecycleReason, suspendDays, primaryListingId], () => {
    lifecyclePreview.value = null;
    Object.keys(lifecycleCommandKeys).forEach(key => {
      delete lifecycleCommandKeys[key];
    });
  });

  return {
    listingLoading,
    listingError,
    lifecyclePreview,
    lifecyclePreviewLoading,
    lifecycleLoading,
    lifecycleReason,
    suspendDays,
    versionSuccessMessage,
    primaryListingId,
    currentListing,
    listingStatusLabel,
    rentalAdministratorLabel,
    listingSourceLabel,
    visitasPermitidasLabel,
    listingVersionRecords,
    latestListingVersion,
    listingLifecycleTimeline,
    latestListingDiffEntries,
    lifecyclePreviewDiffEntries,
    lifecyclePreviewLabel,
    listingVersionEvidenceLabel,
    fetchListingLifecycle,
    lifecyclePreviewMatches,
    prepareOrRunLifecycleAction,
    runLifecycleAction,
  };
}

export default useImovelListingLifecycle;
